/**
 * Connect > Partner detail at /connect/partners/:id.
 *
 * Tabbed: Profile (editable basics + Sage/broker mappings), Users (invite,
 * role, enable/disable, reset password), Agreements (list + create + status),
 * Commissions (broker only: statement + payments CRUD), and Activity feed.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Save, Plus, Trash2, KeyRound, ExternalLink } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import { formatDate, formatDateTime, formatDateShort } from '@/lib/dateUtils';
import {
  hasConnectPartners,
  isNotAvailable,
  orgTypeLabel,
  orgStatusTone,
  agreementStatusLabel,
  connectMoney,
  useConnectOrg,
  usePatchConnectOrg,
  usePutOrgCustomers,
  usePutOrgSalespersons,
  useCreateConnectUser,
  usePatchConnectUser,
  useResetConnectUserPassword,
  useCreateConnectAgreement,
  usePatchConnectAgreement,
  useConnectCommissionStatement,
  useConnectCommissionPayments,
  useCreateCommissionPayment,
  usePatchCommissionPayment,
  useDeleteCommissionPayment,
  useImpersonateOrg,
  AGREEMENT_STATUS_TONES,
  ORG_TYPE_TONES,
  type ConnectAgreement,
  type ConnectAgreementStatus,
  type ConnectUser,
  type CommissionPayment,
  type PricingBucket,
  type FreightBasis,
  type SageCustomerRef,
  type SalespersonCodeRef,
} from '@/lib/useConnect';
import {
  AccessDeniedCard,
  BackLink,
  ConnectHeader,
  ErrorCard,
  Modal,
  NotAvailableCard,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './connectShared';
import { CustomerMappingEditor, SalespersonCodeEditor } from './mappingEditors';

const AGREEMENT_STATUSES: ConnectAgreementStatus[] = [
  'draft', 'sent', 'signed', 'declined', 'voided', 'expired',
];

type Tab = 'profile' | 'users' | 'agreements' | 'commissions' | 'activity';

export function ConnectPartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  // No more read/write levels: holding connect.partners grants both.
  const canRead = hasConnectPartners(user?.permissions, user?.superAdmin);
  const canWrite = canRead;

  const detailQ = useConnectOrg(id);
  const [tab, setTab] = useState<Tab>('profile');

  if (user && !canRead) {
    return (
      <div className="flex h-full min-h-0 flex-col pb-4">
        <BackLink to="/connect" label="Back to partners" />
        <AccessDeniedCard />
      </div>
    );
  }

  if (isNotAvailable(detailQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col pb-4">
        <BackLink to="/connect" label="Back to partners" />
        <NotAvailableCard what="Partner detail" />
      </div>
    );
  }

  const data = detailQ.data;
  const org = data?.org;
  const isBroker = org?.org_type === 'broker';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'users', label: 'Users' },
    { id: 'agreements', label: 'Agreements' },
    ...(isBroker ? [{ id: 'commissions' as Tab, label: 'Commissions' }] : []),
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col pb-4">
      <BackLink to="/connect" label="Back to partners" />
      <ConnectHeader
        eyebrow="Connect / Partners"
        title={org?.name ?? 'Partner'}
        subtitle={undefined}
        actions={
          org && (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <Pill tone={ORG_TYPE_TONES[org.org_type]}>{orgTypeLabel(org.org_type)}</Pill>
                <Pill tone={orgStatusTone(org.status)}>{org.status || '--'}</Pill>
                <ImpersonateButton orgId={org.id} />
              </div>
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                Opens a 1-hour staff view of this partner's portal.
              </p>
            </div>
          )
        }
      />

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading partner...</p>
      ) : detailQ.error ? (
        <ErrorCard message={detailQ.error.message} />
      ) : data && org ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-4 flex gap-1 border-b border-amp-line dark:border-amp-dark-line">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                  tab === t.id
                    ? 'border-amp-olive text-amp-ink font-medium dark:border-amp-olive-dark dark:text-amp-dark-ink'
                    : 'border-transparent text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {tab === 'profile' && (
              <ProfileTab
                orgId={org.id}
                org={org}
                canWrite={canWrite}
              />
            )}
            {tab === 'users' && (
              <UsersTab orgId={org.id} users={data.users} canWrite={canWrite} />
            )}
            {tab === 'agreements' && (
              <AgreementsTab orgId={org.id} agreements={data.agreements} canWrite={canWrite} />
            )}
            {tab === 'commissions' && isBroker && (
              <CommissionsTab orgId={org.id} canWrite={canWrite} />
            )}
            {tab === 'activity' && <ActivityTab activity={data.activity} />}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------- Impersonate button ---------------------------------------------

// "Open portal as partner": mints a 1-hour staff view and opens the partner's
// portal in a new tab. The endpoint is deployed in parallel; until it is live
// the first click 404s and the button then shows a quiet disabled "not
// available yet" state (the isNotAvailable degrade pattern).
function ImpersonateButton({ orgId }: { orgId: number }) {
  const impersonate = useImpersonateOrg(orgId);
  const unavailable = isNotAvailable(impersonate.error);

  const onClick = () => {
    impersonate.mutate(undefined, {
      onSuccess: (res) => {
        if (res.portal_url) window.open(res.portal_url, '_blank', 'noopener');
      },
    });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={impersonate.isPending || unavailable}
      title={unavailable ? 'Not available yet' : 'Open portal as partner'}
      className={secondaryBtnCls}
    >
      {impersonate.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <ExternalLink className="h-3 w-3" />
      )}
      Open portal as partner
    </button>
  );
}

// ---------- Profile tab ----------------------------------------------------

function ProfileTab({
  orgId,
  org,
  canWrite,
}: {
  orgId: number;
  org: {
    name: string;
    status: string;
    org_type: 'wholesaler' | 'broker';
    pricing_bucket: PricingBucket | null;
    freight_basis: FreightBasis | null;
    sage_customers: SageCustomerRef[];
    salesperson_codes: SalespersonCodeRef[];
  };
  canWrite: boolean;
}) {
  const patch = usePatchConnectOrg(orgId);
  const putCustomers = usePutOrgCustomers(orgId);
  const putSalespersons = usePutOrgSalespersons(orgId);

  const seed = useMemo(
    () => ({
      name: org.name,
      status: org.status,
      pricing_bucket: org.pricing_bucket ?? 'wholesale',
      freight_basis: org.freight_basis ?? 'fob',
    }),
    [org.name, org.status, org.pricing_bucket, org.freight_basis],
  );
  const [form, setForm] = useState(seed);
  useEffect(() => setForm(seed), [seed]);

  const [customers, setCustomers] = useState<SageCustomerRef[]>(org.sage_customers ?? []);
  const [codes, setCodes] = useState<SalespersonCodeRef[]>(org.salesperson_codes ?? []);
  useEffect(() => setCustomers(org.sage_customers ?? []), [org.sage_customers]);
  useEffect(() => setCodes(org.salesperson_codes ?? []), [org.salesperson_codes]);

  const basicsDirty =
    form.name !== seed.name ||
    form.status !== seed.status ||
    (org.org_type === 'wholesaler' &&
      (form.pricing_bucket !== seed.pricing_bucket || form.freight_basis !== seed.freight_basis));

  const saveBasics = () => {
    patch.mutate({
      name: form.name.trim(),
      status: form.status,
      ...(org.org_type === 'wholesaler'
        ? { pricing_bucket: form.pricing_bucket as PricingBucket, freight_basis: form.freight_basis as FreightBasis }
        : {}),
    });
  };

  const saveCustomers = () =>
    putCustomers.mutate({
      customers: customers.map((c) => ({
        division_no: c.division_no || undefined,
        customer_no: c.customer_no,
        is_primary: c.is_primary,
      })),
    });

  const saveCodes = () =>
    putSalespersons.mutate({
      codes: codes.map((c) => ({
        division_no: c.division_no || undefined,
        salesperson_no: c.salesperson_no,
        commission_rate: c.commission_rate ?? undefined,
      })),
    });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Partner settings">
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Name</label>
            <input disabled={!canWrite} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Status</label>
              <select disabled={!canWrite} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="prospect">Prospect</option>
              </select>
            </div>
            {org.org_type === 'wholesaler' && (
              <div>
                <label className={labelCls}>Pricing bucket</label>
                <select disabled={!canWrite} value={form.pricing_bucket} onChange={(e) => setForm((f) => ({ ...f, pricing_bucket: e.target.value as PricingBucket }))} className={inputCls}>
                  <option value="wholesale">Wholesale</option>
                  <option value="distributor">Distributor</option>
                </select>
              </div>
            )}
          </div>
          {org.org_type === 'wholesaler' && (
            <div>
              <label className={labelCls}>Freight basis</label>
              <select disabled={!canWrite} value={form.freight_basis} onChange={(e) => setForm((f) => ({ ...f, freight_basis: e.target.value as FreightBasis }))} className={`${inputCls} max-w-[200px]`}>
                <option value="fob">FOB</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          )}
          {patch.isError && <p className="text-xs text-red-700 dark:text-red-300">{patch.error.message}</p>}
          {patch.isSuccess && !basicsDirty && <p className="text-xs text-green-700 dark:text-green-300">Saved.</p>}
          {canWrite && (
            <button type="button" onClick={saveBasics} disabled={patch.isPending || !basicsDirty} className={primaryBtnCls}>
              {patch.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save settings
            </button>
          )}
        </div>
      </Card>

      <Card title={org.org_type === 'broker' ? 'Salesperson codes' : 'Sage customer mappings'}>
        {org.org_type === 'broker' ? (
          <>
            <SalespersonCodeEditor value={codes} onChange={setCodes} disabled={!canWrite} />
            {putSalespersons.isError && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{putSalespersons.error.message}</p>}
            {canWrite && (
              <button type="button" onClick={saveCodes} disabled={putSalespersons.isPending} className={`${primaryBtnCls} mt-3`}>
                {putSalespersons.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save codes
              </button>
            )}
          </>
        ) : (
          <>
            <CustomerMappingEditor value={customers} onChange={setCustomers} disabled={!canWrite} />
            {putCustomers.isError && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{putCustomers.error.message}</p>}
            {canWrite && (
              <button type="button" onClick={saveCustomers} disabled={putCustomers.isPending} className={`${primaryBtnCls} mt-3`}>
                {putCustomers.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save mappings
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ---------- Users tab ------------------------------------------------------

function UsersTab({ orgId, users, canWrite }: { orgId: number; users: ConnectUser[]; canWrite: boolean }) {
  const [addOpen, setAddOpen] = useState(false);
  const patch = usePatchConnectUser(orgId);
  const reset = useResetConnectUserPassword();
  const [notice, setNotice] = useState<string | null>(null);

  const activeAdmins = users.filter((u) => u.role === 'org-admin' && u.status === 'active').length;

  const toggleStatus = (u: ConnectUser) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    if (next === 'disabled' && u.role === 'org-admin' && activeAdmins <= 1) {
      setNotice('This is the last active org-admin. Promote another user before disabling this one.');
      return;
    }
    setNotice(null);
    patch.mutate({ userId: u.id, body: { status: next } }, {
      onError: (e) => setNotice(e.message),
    });
  };

  const changeRole = (u: ConnectUser, role: string) => {
    setNotice(null);
    patch.mutate({ userId: u.id, body: { role } }, { onError: (e) => setNotice(e.message) });
  };

  const resetPw = (u: ConnectUser) => {
    setNotice(null);
    reset.mutate(u.id, {
      onSuccess: () => setNotice(`Temporary password emailed to ${u.email}.`),
      onError: (e) => setNotice(isNotAvailable(e) ? 'Password reset is not available yet.' : e.message),
    });
  };

  return (
    <Card
      title="Users"
      action={canWrite ? (
        <button type="button" onClick={() => setAddOpen(true)} className={secondaryBtnCls}>
          <Plus className="h-3 w-3" /> Add user
        </button>
      ) : undefined}
    >
      {notice && (
        <div className="mb-3 rounded border border-amp-line bg-amp-cream-warm px-3 py-2 text-xs text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink" role="status">
          {notice}
        </div>
      )}
      {users.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No users yet.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Email</th>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium w-[140px]">Role</th>
              <th className="px-3 py-2 text-left font-medium w-[110px]">Status</th>
              <th className="px-3 py-2 text-left font-medium w-[150px]">Last login</th>
              <th className="px-3 py-2 text-right font-medium w-[200px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{u.email}</td>
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{u.name ?? '--'}</td>
                <td className="px-3 py-2">
                  {canWrite ? (
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="border border-amp-line rounded px-2 py-1 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink">
                      <option value="org-admin">Org admin</option>
                      <option value="buyer">Buyer</option>
                    </select>
                  ) : (
                    <span className="text-amp-ink dark:text-amp-dark-ink">{u.role}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Pill tone={u.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300'}>{u.status}</Pill>
                </td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{u.last_login_at ? formatDateShort(u.last_login_at) : 'never'}</td>
                <td className="px-3 py-2 text-right">
                  {canWrite && (
                    <div className="inline-flex items-center gap-2">
                      <button type="button" onClick={() => resetPw(u)} className="inline-flex items-center gap-1 text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink" title="Reset password">
                        <KeyRound className="h-3.5 w-3.5" /> Reset
                      </button>
                      <button type="button" onClick={() => toggleStatus(u)} className={secondaryBtnCls}>
                        {u.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {addOpen && <AddUserModal orgId={orgId} onClose={() => setAddOpen(false)} onAdded={(email) => setNotice(`Temporary password emailed to ${email}.`)} />}
    </Card>
  );
}

function AddUserModal({ orgId, onClose, onAdded }: { orgId: number; onClose: () => void; onAdded: (email: string) => void }) {
  const create = useCreateConnectUser(orgId);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('buyer');

  const submit = () => {
    create.mutate(
      { email: email.trim(), name: name.trim() || undefined, role },
      {
        onSuccess: () => {
          onAdded(email.trim());
          onClose();
        },
      },
    );
  };

  const errText = create.error
    ? create.error.message.includes('409')
      ? 'A user with that email already exists for this partner.'
      : isNotAvailable(create.error)
        ? 'Adding users is not available yet.'
        : create.error.message
    : null;

  return (
    <Modal
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={submit} disabled={create.isPending || !email.trim()} className={primaryBtnCls}>
            {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Add user
          </button>
        </>
      }
    >
      {errText && <p className="mb-3 text-xs text-red-700 dark:text-red-300">{errText}</p>}
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="buyer@partner.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
              <option value="org-admin">Org admin</option>
              <option value="buyer">Buyer</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">A temporary password is emailed to the user on creation.</p>
      </div>
    </Modal>
  );
}

// ---------- Agreements tab -------------------------------------------------

function AgreementsTab({ orgId, agreements, canWrite }: { orgId: number; agreements: ConnectAgreement[]; canWrite: boolean }) {
  const [addOpen, setAddOpen] = useState(false);
  const patch = usePatchConnectAgreement();

  return (
    <Card
      title="Agreements"
      action={canWrite ? (
        <button type="button" onClick={() => setAddOpen(true)} className={secondaryBtnCls}>
          <Plus className="h-3 w-3" /> New agreement
        </button>
      ) : undefined}
    >
      {agreements.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No agreements yet.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Title</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Type</th>
              <th className="px-3 py-2 text-left font-medium w-[140px]">Status</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Sent</th>
              <th className="px-3 py-2 text-left font-medium w-[120px]">Expires</th>
              <th className="px-3 py-2 text-left font-medium">Document</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {agreements.map((a) => (
              <tr key={a.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                  {a.title}
                  {a.docusign_envelope_id && (
                    <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">Envelope {a.docusign_envelope_id}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{a.agreement_type ?? '--'}</td>
                <td className="px-3 py-2">
                  {canWrite ? (
                    <select
                      value={a.status}
                      onChange={(e) => patch.mutate({ id: a.id, body: { status: e.target.value as ConnectAgreementStatus } })}
                      className="border border-amp-line rounded px-2 py-1 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                    >
                      {AGREEMENT_STATUSES.map((s) => (
                        <option key={s} value={s}>{agreementStatusLabel(s)}</option>
                      ))}
                    </select>
                  ) : (
                    <Pill tone={AGREEMENT_STATUS_TONES[a.status]}>{agreementStatusLabel(a.status)}</Pill>
                  )}
                </td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(a.sent_at)}</td>
                <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(a.expires_at)}</td>
                <td className="px-3 py-2">
                  {a.document_url ? (
                    <a href={a.document_url} target="_blank" rel="noopener noreferrer" className="text-amp-olive hover:underline dark:text-amp-olive-dark">Open</a>
                  ) : (
                    <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {addOpen && <NewAgreementModal orgId={orgId} onClose={() => setAddOpen(false)} />}
    </Card>
  );
}

export function NewAgreementModal({
  orgId,
  onClose,
}: {
  orgId: number;
  onClose: () => void;
}) {
  const create = useCreateConnectAgreement();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  const [signer, setSigner] = useState('');
  const [status, setStatus] = useState<ConnectAgreementStatus>('draft');
  const [expires, setExpires] = useState('');

  const submit = () => {
    create.mutate(
      {
        org_id: orgId,
        title: title.trim(),
        agreement_type: type.trim() || undefined,
        signer_email: signer.trim() || undefined,
        status,
        expires_at: expires || undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="New agreement"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={submit} disabled={create.isPending || !title.trim()} className={primaryBtnCls}>
            {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Create
          </button>
        </>
      }
    >
      {create.error && <p className="mb-3 text-xs text-red-700 dark:text-red-300">{isNotAvailable(create.error) ? 'Agreements are not available yet.' : create.error.message}</p>}
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Type</label>
            <input value={type} onChange={(e) => setType(e.target.value)} className={inputCls} placeholder="terms" />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as ConnectAgreementStatus)} className={inputCls}>
              {AGREEMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{agreementStatusLabel(s)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Signer email</label>
            <input value={signer} onChange={(e) => setSigner(e.target.value)} className={inputCls} placeholder="buyer@partner.com" />
          </div>
          <div>
            <label className={labelCls}>Expires</label>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Commissions tab (broker only) ----------------------------------

function CommissionsTab({ orgId, canWrite }: { orgId: number; canWrite: boolean }) {
  const stmtQ = useConnectCommissionStatement(orgId);
  const paymentsQ = useConnectCommissionPayments(orgId);
  const del = useDeleteCommissionPayment(orgId);
  const [editing, setEditing] = useState<CommissionPayment | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  if (isNotAvailable(stmtQ.error) && isNotAvailable(paymentsQ.error)) {
    return <NotAvailableCard what="Commission tracking" />;
  }

  const stmt = stmtQ.data;
  const payments = paymentsQ.data?.payments ?? stmt?.payments ?? [];

  const removePayment = (p: CommissionPayment) => {
    if (window.confirm(`Delete the ${connectMoney(p.amount)} payment from ${formatDate(p.period_start)}?`)) {
      del.mutate(p.id);
    }
  };

  return (
    <div className="space-y-4">
      <Card title="Commission statement">
        {stmtQ.isLoading ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading statement...</p>
        ) : stmt && stmt.months.length > 0 ? (
          <>
            <table className="min-w-full text-sm">
              <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Month</th>
                  <th className="px-3 py-2 text-right font-medium">Sales</th>
                  <th className="px-3 py-2 text-right font-medium">Accrued commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                {stmt.months.map((m) => (
                  <tr key={m.month}>
                    <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{m.month}</td>
                    <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{connectMoney(m.sales)}</td>
                    <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{connectMoney(m.accrued_commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex flex-wrap gap-6 text-sm">
              {stmt.total_accrued != null && <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Total accrued: <span className="text-amp-ink dark:text-amp-dark-ink">{connectMoney(stmt.total_accrued)}</span></span>}
              {stmt.total_paid != null && <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Total paid: <span className="text-amp-ink dark:text-amp-dark-ink">{connectMoney(stmt.total_paid)}</span></span>}
              {stmt.balance != null && <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Balance: <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{connectMoney(stmt.balance)}</span></span>}
            </div>
          </>
        ) : (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No commission activity yet.</p>
        )}
      </Card>

      <Card
        title="Payments"
        action={canWrite ? (
          <button type="button" onClick={() => setAddOpen(true)} className={secondaryBtnCls}>
            <Plus className="h-3 w-3" /> Add payment
          </button>
        ) : undefined}
      >
        {payments.length === 0 ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No payments recorded.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Period</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Paid</th>
                <th className="px-3 py-2 text-left font-medium">Reference</th>
                <th className="px-3 py-2 text-right font-medium w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                  <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatDateShort(p.period_start)} – {formatDateShort(p.period_end)}</td>
                  <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{connectMoney(p.amount)}</td>
                  <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{p.paid_at ? formatDateShort(p.paid_at) : '--'}</td>
                  <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{p.reference ?? '--'}</td>
                  <td className="px-3 py-2 text-right">
                    {canWrite && (
                      <div className="inline-flex items-center gap-2">
                        <button type="button" onClick={() => setEditing(p)} className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink">Edit</button>
                        <button type="button" onClick={() => removePayment(p)} aria-label="Delete payment" className="text-amp-ink-muted hover:text-red-700 dark:text-amp-dark-ink-muted"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {(addOpen || editing) && (
        <PaymentModal
          orgId={orgId}
          existing={editing}
          onClose={() => { setAddOpen(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function PaymentModal({ orgId, existing, onClose }: { orgId: number; existing: CommissionPayment | null; onClose: () => void }) {
  const create = useCreateCommissionPayment();
  const patch = usePatchCommissionPayment(orgId);
  const [periodStart, setPeriodStart] = useState(existing?.period_start?.slice(0, 10) ?? '');
  const [periodEnd, setPeriodEnd] = useState(existing?.period_end?.slice(0, 10) ?? '');
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '');
  const [paidAt, setPaidAt] = useState(existing?.paid_at?.slice(0, 10) ?? '');
  const [reference, setReference] = useState(existing?.reference ?? '');
  const [note, setNote] = useState(existing?.note ?? '');

  const busy = create.isPending || patch.isPending;
  const err = create.error || patch.error;

  const submit = () => {
    const body = {
      org_id: orgId,
      period_start: periodStart,
      period_end: periodEnd,
      amount: Number(amount),
      paid_at: paidAt || undefined,
      reference: reference.trim() || undefined,
      note: note.trim() || undefined,
    };
    if (existing) {
      patch.mutate({ id: existing.id, body }, { onSuccess: onClose });
    } else {
      create.mutate(body, { onSuccess: onClose });
    }
  };

  const valid = periodStart && periodEnd && amount !== '' && Number.isFinite(Number(amount));

  return (
    <Modal
      title={existing ? 'Edit payment' : 'Add payment'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy || !valid} className={primaryBtnCls}>
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} {existing ? 'Save' : 'Add'}
          </button>
        </>
      }
    >
      {err && <p className="mb-3 text-xs text-red-700 dark:text-red-300">{err.message}</p>}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Period start</label>
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Period end</label>
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Amount</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0.00" />
          </div>
          <div>
            <label className={labelCls}>Paid on</label>
            <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Reference</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls} placeholder="Check #, ACH id..." />
        </div>
        <div>
          <label className={labelCls}>Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
        </div>
      </div>
    </Modal>
  );
}

// ---------- Activity tab ---------------------------------------------------

function ActivityTab({ activity }: { activity: { actor_email: string; action: string; detail: string | null; created_at: string }[] }) {
  return (
    <Card title="Activity">
      {activity.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No activity recorded.</p>
      ) : (
        <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {activity.map((a, i) => (
            <li key={i} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
                  <span className="font-medium">{a.actor_email}</span> {a.action}
                </p>
                {a.detail && <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{a.detail}</p>}
              </div>
              <span className="shrink-0 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap" title={formatDateTime(a.created_at)}>
                {formatDateTime(a.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------- shared card ----------------------------------------------------

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}
